import React, { FC, useCallback, useContext, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Trans, useTranslation } from 'react-i18next';
import { OrganizationContext } from '../../contexts/OrganizationContext';
import { apiOrganizationProduct } from '../../resources/api/apiOrganizationProduct';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { Dayjs } from 'dayjs';
import { OrganizationProductsFetcher } from '../Customer/CustomerFetchers';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { TogglableDateFormPartial } from '../TogglableDateFormPartial/TogglableDateFormPartial';
import { IOrganizationProduct } from '../../resources/models/IOrganizationProduct';
import { CustomerProductLicensesFetcher } from '../../fetchers/CustomerProductLicensesFetcher';

interface IProps {
  open: boolean;
  closeDialog: () => void;
  customerProduct: IOrganizationProduct;
}

const { useSuspendOrganizationProduct } = apiOrganizationProduct;

export const CustomerProductSuspend: FC<IProps> = ({ open, closeDialog, customerProduct }) => {
  const { onRefresh } = useContext(OrganizationProductsFetcher.Context);
  const { onRefresh: onRefreshLicenses } = useContext(CustomerProductLicensesFetcher.Context);
  const { organization } = useContext(OrganizationContext);

  const handleError = useApiErrorHandler();
  const { t } = useTranslation();
  const [schedule, setSchedule] = useState<Dayjs | null>(null);

  const { execute: suspendCustomerProduct } = useSuspendOrganizationProduct(organization ?? void 0, customerProduct.id);

  const suspend = useCallback(async () => {
    try {
      await suspendCustomerProduct({
        scheduleTime: schedule?.valueOf(),
      });
      await Promise.all([onRefresh(), onRefreshLicenses()]);
      closeDialog();
    } catch (e) {
      handleError(e);
    }
  }, [suspendCustomerProduct, schedule]);

  return (
    <Dialog open={open} onClose={closeDialog}>
      <DialogTitle>{t('customerProduct.suspendDialog.title')}</DialogTitle>
      <DialogContent>
        <Trans i18nKey="customerProduct.suspendDialog.message">
          <Typography paragraph />
          <Typography paragraph />
        </Trans>
        <Grid container>
          <TogglableDateFormPartial
            value={schedule}
            onChange={setSchedule}
            togglerLabel={t('customerProduct.suspendDialog.scheduleLabel')}
            disablePast
          />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog}>{t('common.dialog.close')}</Button>
        <Button variant="contained" color={schedule ? 'secondary' : 'primary'} onClick={suspend}>
          {schedule
            ? t('customerProduct.suspendDialog.confirmSchedule')
            : t('customerProduct.suspendDialog.confirmNow')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
